/**
 * The redaction contract. Ordering rule (PLAN-OBSERVABILITY.md): the scrubber
 * is written BEFORE the exporter is enabled — a telemetry vendor's retention
 * cannot be undone by deploying a fix.
 *
 * Never log or attach to a crash report:
 *
 *   message bodies · practice conversations · reflection text · precise
 *   coordinates · access/refresh tokens · raw authorization or cookie headers ·
 *   password, OTP and reset links · payment details · provider secrets ·
 *   signed S3 URLs · full push subscriptions · full user profiles
 *
 * THIS FILE HAS A TWIN: backend/api/src/common/redaction/redact.ts is a
 * byte-identical copy below the header — duplicated because this app cannot
 * import outside its tree (no monorepo Metro setup, and public.ts documents
 * that boundary), and a copy whose equivalence is asserted is safer than a
 * Metro resolution change for a security module. It also imports nothing so
 * root `node --test` can load it. tests/redaction/redaction.test.ts runs both
 * copies over one corpus and fails if they disagree. Edit one, edit both.
 *
 * THREE MECHANISMS, none a fallback for the others:
 * - `redactValue()` removes whole fields by key (right for a DTO, where
 *   `text` IS the user's message).
 * - `redactText()` sanitises a string in place (an error's own message is the
 *   diagnostic value; it removes what is recognisable and keeps the rest).
 * - `scrubSentryEvent()` cleans the exporter's envelope, whose forbidden
 *   fields the SDK populates on its own.
 *
 * The classification is docs/observability/SENTRY_DATA_POLICY.md — the
 * authority; this file is one of its two enforcement points (the other is
 * Sentry's org-level server-side scrubbing).
 */

export const REDACTED = "[redacted]";

/** Values longer than this are truncated; a stack frame is well under it. */
const MAX_STRING_LENGTH = 1024;

/** Beyond this depth a payload is a document, not a diagnostic. */
const MAX_DEPTH = 8;

/** Guards against a whole collection arriving as one breadcrumb. */
const MAX_ARRAY_LENGTH = 50;
const MAX_OBJECT_KEYS = 100;

/**
 * Keys whose VALUE is never safe, whatever it contains.
 *
 * Matched case-insensitively against the key with separators removed, so
 * `access_token`, `accessToken` and `ACCESS-TOKEN` are one entry.
 *
 * `message`, `text` and `body` are here deliberately. In this codebase those
 * are the names of user-authored content (`chat.dto.ts`, `reflection.dto.ts`,
 * the practice turn DTOs). An error's own message does not travel as an object
 * property — it arrives as the exception value and goes through `redactText()`.
 */
const DENIED_KEYS = new Set([
  // Message bodies, practice conversations, reflection text
  "message",
  "messages",
  "messagebody",
  "chatbody",
  "text",
  "body",
  "content",
  "note",
  "notes",
  "reply",
  "prompt",
  "completion",
  "transcript",
  "conversation",
  "reflection",
  "reflectiontext",
  "practicetext",
  "answer",
  "answers",
  "opener",
  "draft",
  // Raw request/response bodies and SQL parameter values
  "requestbody",
  "responsebody",
  "rawbody",
  "sql",
  "bindings",
  "parameters",
  // Images and attachments. A photo URL is as disclosing as the photo —
  // exposing one is forbidden outright elsewhere in this codebase.
  "image",
  "images",
  "imageurl",
  "photo",
  "photos",
  "photourl",
  "attachment",
  "attachments",
  "signedurl",
  "presignedurl",
  // Precise coordinates
  "lat",
  "lng",
  "lon",
  "latitude",
  "longitude",
  "coordinates",
  "coords",
  "location",
  "preciselocation",
  "locationpayload",
  "point",
  "geometry",
  "address",
  "street",
  // Credentials
  "token",
  "accesstoken",
  "refreshtoken",
  "idtoken",
  "oauthtoken",
  "authorization",
  "auth",
  "cookie",
  "cookies",
  "setcookie",
  "password",
  "passwordhash",
  "otp",
  "code",
  "secret",
  "clientsecret",
  "apikey",
  "privatekey",
  "signature",
  "sessiontoken",
  // Payment
  "card",
  "cardnumber",
  "pan",
  "cvc",
  "cvv",
  "iban",
  "paymentmethod",
  // Push subscriptions
  "endpoint",
  "subscription",
  "p256dh",
  "keys",
  // Identity. The one identifier that IS allowed is an opaque user id, which
  // is why `id` and `userId` are deliberately absent from this list.
  "email",
  "displayname",
  "firstname",
  "lastname",
  "fullname",
  "phone",
  "phonenumber",
  "dob",
  "dateofbirth",
  "birthdate",
]);

/** Normalises a key for denylist comparison: lowercase, separators removed. */
function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[-_\s]/g, "");
}

export function isDeniedKey(key: string): boolean {
  return DENIED_KEYS.has(normalizeKey(key));
}

/**
 * Patterns removed from any string that survives.
 *
 * Ordered deliberately: the URL rules run before the bare-token rule so a
 * signed URL is replaced whole rather than having its signature picked out of
 * it and the rest of the path — which names a bucket and an object — kept.
 */
const TEXT_PATTERNS: { pattern: RegExp; replacement: string }[] = [
  // Signed S3 / presigned URLs. Matched by their query parameters, since the
  // host is not reliably distinguishable from any other https URL.
  {
    pattern: /https?:\/\/\S*?[?&](?:X-Amz-Signature|X-Amz-Credential|Signature|AWSAccessKeyId)=\S*/gi,
    replacement: "[redacted-signed-url]",
  },
  // Password reset, magic link and OAuth callbacks carrying a credential.
  {
    pattern: /https?:\/\/\S*?[?&](?:token|code|otp|reset|access_token|id_token)=\S*/gi,
    replacement: "[redacted-link]",
  },
  // `Authorization: Bearer …`, and the bare header value.
  {
    pattern: /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi,
    replacement: "Bearer [redacted]",
  },
  // JWTs anywhere, including ones not introduced by a header name.
  {
    pattern: /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+/g,
    replacement: "[redacted-jwt]",
  },
  // Provider secrets by their documented prefixes.
  {
    pattern: /\b(?:sk|rk|whsec|pk)_(?:live|test)_[A-Za-z0-9]{8,}/g,
    replacement: "[redacted-key]",
  },
  // Credentials embedded in a connection string — `postgres://user:pass@host`,
  // `redis://:pass@host`, `smtp://user:pass@host`. These reach an error message
  // whenever a dependency is unreachable, which is the most common server error
  // there is, and the host is kept because it is the diagnostic.
  //
  // Before this rule the password survived unless the host happened to contain
  // a dot, because the only thing removing it was the email pattern below
  // matching `pass@host.tld` by accident. Every hostname in this repo's compose
  // file (`postgres`, `redis`, `minio`) and the `localhost` default in
  // `environments/*/server.yaml` are dotless, so the accident did not apply
  // exactly where it was needed. Found by a test written for the bootstrap
  // failure path, where an unreachable database is the expected event.
  //
  // Ordered before the email pattern so the whole userinfo goes at once rather
  // than the password being replaced with `[redacted-email]`.
  {
    pattern: /\b([a-z][a-z0-9+.-]*:\/\/)[^\s/:@]*:[^\s/@]+@/gi,
    replacement: "$1[redacted-credentials]@",
  },
  // Email addresses.
  {
    pattern: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
    replacement: "[redacted-email]",
  },
  // A decimal coordinate pair. Requires a fractional part on both numbers, so
  // "took 3, returned 4" is untouched while "51.5074, -0.1278" is not.
  {
    pattern: /-?\d{1,3}\.\d{3,}\s*,\s*-?\d{1,3}\.\d{3,}/g,
    replacement: "[redacted-coordinates]",
  },
  // Long digit runs: card numbers, and any other unbroken identifier of that
  // shape. Kept last so it cannot eat part of an earlier match.
  {
    pattern: /\b\d[\d -]{11,21}\d\b/g,
    replacement: "[redacted-number]",
  },
];

/**
 * A URL reduced to what is safe to keep: scheme, host and path.
 *
 * The query string is dropped wholesale rather than filtered. Reset tokens,
 * OTPs, OAuth codes, signed-URL signatures and email addresses all travel
 * there, and an allow-list of "safe" parameters is a list somebody has to keep
 * correct forever. The path is still scrubbed, because a path segment is
 * sometimes an email (`/users/someone@example.com`).
 *
 * A string that does not parse is treated as free text rather than discarded —
 * it is usually a route template or a relative path, both of which are
 * explicitly allowed.
 */
export function sanitizeUrl(url: string): string {
  const [withoutFragment] = url.split("#", 1);
  const [path, query] = (withoutFragment ?? "").split("?", 2);
  const raw = path ?? "";

  // Percent-encoding defeats every pattern in this file: `someone%40example.com`
  // is an email address that no email pattern matches, and a URL path is the one
  // place a value arrives pre-encoded. So the path is tried as written first,
  // and only if that finds nothing are its decoded forms tried — a path that is
  // already clean is therefore kept exactly as it was, encoding and all.
  let clean = redactText(raw);
  if (clean === raw) {
    for (const form of decodedForms(raw).slice(1)) {
      const redacted = redactText(form);
      if (redacted !== form) {
        clean = redacted;
        break;
      }
    }
  }

  return query === undefined ? clean : `${clean}?[redacted]`;
}

/**
 * A string and its successive percent-decodings, original first.
 *
 * Bounded at two passes: `%2540` needs two to reveal an `@`, and nothing
 * legitimate needs more. `decodeURIComponent` throws on a malformed escape
 * (`%ZZ`), which ends the walk rather than the request.
 */
function decodedForms(value: string): string[] {
  const forms = [value];
  let current = value;
  for (let i = 0; i < 2; i += 1) {
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      break;
    }
    if (next === current) break;
    forms.push(next);
    current = next;
  }
  return forms;
}

/**
 * Sanitises a single free-text string.
 *
 * Used for error messages and stack frames, where the string IS the diagnostic
 * and removing it wholesale would leave nothing to report.
 */
export function redactText(value: string): string {
  let result = value;
  for (const { pattern, replacement } of TEXT_PATTERNS) {
    result = result.replace(pattern, replacement);
  }
  return truncate(result);
}

function truncate(value: string): string {
  return value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}… [truncated ${value.length - MAX_STRING_LENGTH} chars]`
    : value;
}

/**
 * Deep-copies `input` with denied fields removed and surviving strings
 * sanitised.
 *
 * Structure is preserved on purpose: a report saying `{ text: "[redacted]" }`
 * tells you the field was present, which is diagnostically useful and discloses
 * nothing. Dropping the key entirely would make an absent field and a redacted
 * one indistinguishable.
 */
export function redactValue(
  input: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): unknown {
  if (input === null || input === undefined) return input;

  if (depth >= MAX_DEPTH) return "[truncated-depth]";

  // Cycles. A depth cap alone would terminate, but it would also spend eight
  // levels serialising the same object and then report a truncation that reads
  // like a deep payload rather than a loop. Error objects carrying a reference
  // back to a request, and ORM entities with both sides of a relation loaded,
  // are the two that actually occur here.
  if (typeof input === "object") {
    if (seen.has(input as object)) return "[circular]";
    seen.add(input as object);
  }

  const type = typeof input;

  if (type === "string") return redactText(input as string);
  if (type === "number" || type === "boolean") return input;
  if (type === "bigint") return `${(input as bigint).toString()}n`;
  if (type === "function" || type === "symbol") return `[${type}]`;

  if (input instanceof Date) return input.toISOString();
  if (input instanceof Error) return redactError(input, depth, seen);

  if (Array.isArray(input)) {
    const kept = input.slice(0, MAX_ARRAY_LENGTH)
      .map((item) => redactValue(item, depth + 1, seen));
    return input.length > MAX_ARRAY_LENGTH
      ? [...kept, `[${input.length - MAX_ARRAY_LENGTH} more]`]
      : kept;
  }

  if (input instanceof Map || input instanceof Set) {
    return redactValue(
      input instanceof Map ? Object.fromEntries(input) : [...input],
      depth,
      seen,
    );
  }

  if (type === "object") {
    const source = input as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    // Named `kept`, not `seen` — `seen` is the cycle tracker above, and
    // shadowing it here silently passed a number into the recursion.
    let kept = 0;

    for (const key of Object.keys(source)) {
      if (kept >= MAX_OBJECT_KEYS) {
        out["[truncated]"] = `${Object.keys(source).length - kept} more keys`;
        break;
      }
      kept += 1;
      out[key] = isDeniedKey(key)
        ? REDACTED
        : redactValue(source[key], depth + 1, seen);
    }
    return out;
  }

  return "[unknown]";
}

/**
 * An Error, reduced to what is safe and still worth having.
 *
 * `name`, `message` and `stack` are kept (sanitised) because they are the
 * report. Any other enumerable property — the ones frameworks attach, which is
 * where a request body or a query result ends up — goes through the object
 * rules.
 */
export function redactError(
  error: Error,
  depth = 0,
  seen: WeakSet<object> = new WeakSet(),
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    name: error.name,
    message: redactText(error.message),
  };

  if (error.stack) out.stack = redactText(error.stack);

  for (const key of Object.keys(error)) {
    if (key === "name" || key === "message" || key === "stack") continue;
    out[key] = isDeniedKey(key)
      ? REDACTED
      : redactValue(
          (error as unknown as Record<string, unknown>)[key],
          depth + 1,
          seen,
        );
  }

  return out;
}

/**
 * ── The Sentry envelope ──────────────────────────────────────────────────────
 *
 * `redactValue()` handles arbitrary payloads. A Sentry event is not arbitrary:
 * it has a fixed shape, and several of its fields are whole categories of
 * forbidden data that arrive **automatically** rather than because someone
 * attached them. `request.headers` carries the authorization header and
 * cookies; `request.data` is the raw body; `user` is populated with whatever
 * the SDK can find, including an IP address. None of that is opted into, so
 * none of it can be prevented by remembering not to attach something.
 *
 * The classification this implements is recorded in
 * `docs/observability/SENTRY_DATA_POLICY.md`: an opaque user id, technical
 * identifiers and a sanitised exception are allowed; identity, location,
 * message content and credentials are not.
 *
 * This runs in the SDK's `beforeSend` — the last point at which an event is
 * still ours. It is the second of two layers, not the only one: Sentry's
 * organisation-level scrubbing is configured as well, because a client-side
 * scrubber cannot protect against a client that fails to run it.
 */

/** The subset of a Sentry event this needs to know about. */
export type SentryEventLike = {
  user?: Record<string, unknown> | null;
  request?: Record<string, unknown> | null;
  extra?: Record<string, unknown> | null;
  contexts?: Record<string, unknown> | null;
  breadcrumbs?: unknown[] | null;
  exception?: unknown;
  message?: unknown;
  tags?: Record<string, unknown> | null;
  [key: string]: unknown;
};

/**
 * Context keys dropped entirely rather than redacted.
 *
 * `profile` is a full user profile and `location`/`geo` are coordinates — all
 * forbidden outright, so there is nothing inside worth keeping. Everything else
 * in `contexts` (os, device, app, runtime) is on the allowed list, which is why
 * contexts is filtered rather than dropped wholesale.
 */
const DROPPED_CONTEXTS = new Set(["profile", "location", "geo"]);

/**
 * `extra` keys dropped entirely.
 *
 * These are the names under which whole request/response payloads get attached
 * by hand. Redacting them field by field would preserve their shape, which is
 * not worth the risk of one unlisted key inside.
 */
const DROPPED_EXTRA = new Set([
  "request",
  "response",
  "body",
  "user",
  "payload",
  "input",
  "output",
]);

/** A breadcrumb, with its message sanitised and its data redacted. */
function scrubBreadcrumb(crumb: unknown): unknown {
  if (!crumb || typeof crumb !== "object") return redactValue(crumb);
  const source = crumb as Record<string, unknown>;

  const out: Record<string, unknown> = {};
  for (const key of ["type", "category", "level", "timestamp", "event_id"]) {
    if (source[key] !== undefined) out[key] = source[key];
  }
  // A breadcrumb message is usually a URL or an interpolated string, so it is
  // sanitised rather than kept whole or dropped.
  if (typeof source.message === "string") {
    out.message = redactText(sanitizeUrl(source.message));
  }
  if (source.data !== undefined) out.data = scrubBreadcrumbData(source.data);
  return out;
}

/**
 * Keys inside `breadcrumb.data` whose value is a URL.
 *
 * The SDK's HTTP integration uses `url`; the others are what the same data
 * arrives as from fetch/XHR instrumentation and redirects.
 */
const URL_DATA_KEYS = new Set(["url", "href", "uri", "endpoint"]);

/**
 * `breadcrumb.data`, with any URL inside it sanitised rather than merely
 * redacted.
 *
 * This exists because of a confirmed leak. Every outgoing HTTP call the API
 * makes becomes a breadcrumb automatically, and `redactValue` alone does not
 * drop query strings — it only matches patterns. Our provider calls put both
 * `apiKey=` and the user's `lat=`/`lon=` in the query string
 * (`modules/venues/places.service.ts`, which already redacts the key in its own
 * logs), so a credential and a precise location were reaching Sentry attached
 * to an unrelated error. `sanitizeUrl` drops the whole query string, which is
 * what makes both safe at once.
 */
function scrubBreadcrumbData(data: unknown): unknown {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return redactValue(data);
  }

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (URL_DATA_KEYS.has(normalizeKey(key)) && typeof value === "string") {
      out[key] = redactText(sanitizeUrl(value));
      continue;
    }
    out[key] = isDeniedKey(key) ? REDACTED : redactValue(value);
  }
  return out;
}

/**
 * Returns a redacted **copy** of a Sentry event.
 *
 * Never mutates its input: the event is a shared object the SDK may still hold
 * a reference to, and mutating inside `beforeSend` makes the scrubber's effect
 * depend on call order.
 */
export function scrubSentryEvent<T extends object>(event: T): T {
  // Typed loosely on the way in and cast back on the way out. The SDK's own
  // `ErrorEvent` has concrete field types with no index signatures, so it is
  // not assignable to `SentryEventLike` however that type is written —
  // `SentryEventLike` documents the shape this reads, it does not gate it.
  const source = event as unknown as SentryEventLike;
  const out = { ...source } as SentryEventLike;

  // An opaque id and nothing else. `ip_address`, `email` and `username` are
  // populated by the SDK without being asked for.
  const userId = source.user?.id;
  out.user =
    userId === undefined || userId === null ? undefined : { id: String(userId) };

  if (source.request) {
    const request = source.request;
    const scrubbed: Record<string, unknown> = {};
    // Kept: what the request WAS. Dropped: everything it carried — data,
    // cookies, headers, query_string.
    if (typeof request.method === "string") scrubbed.method = request.method;
    if (typeof request.url === "string") scrubbed.url = sanitizeUrl(request.url);
    out.request = scrubbed;
  }

  if (source.extra) {
    const extra: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(source.extra)) {
      if (DROPPED_EXTRA.has(key.toLowerCase())) continue;
      extra[key] = isDeniedKey(key) ? REDACTED : redactValue(value);
    }
    out.extra = extra;
  }

  if (source.contexts) {
    const contexts: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(source.contexts)) {
      if (DROPPED_CONTEXTS.has(key.toLowerCase())) continue;
      contexts[key] = redactValue(value);
    }
    out.contexts = contexts;
  }

  if (Array.isArray(source.breadcrumbs)) {
    out.breadcrumbs = source.breadcrumbs.map(scrubBreadcrumb);
  }

  // The exception is the report. Types and stack frames survive; only the
  // human-readable value is sanitised, because that is where user input gets
  // interpolated.
  if (source.exception) out.exception = redactValue(source.exception);
  if (typeof source.message === "string") out.message = redactText(source.message);
  if (source.tags) out.tags = redactValue(source.tags) as Record<string, unknown>;

  return out as unknown as T;
}
